import { useCallback } from 'react';
import { Editor, Range, Transforms } from 'slate';
import { calculateTagSegments, insertTag, insertTagWithIds } from '../utils/insertion';
import { isTag } from '../utils/navigation';
import { findBlockCarrierForTag } from '../utils/blockTagging';
import { CreateAndInsertTagInput, ContinuationTagRef, SuccessorTagRef, TagPersistence } from '../types';
import { CreateTagInput, useCreateTag, useContinueTag, useUpdateTag, useDiscardTags } from 'entities/tag';

/**
 * Public API of the create-and-insert flow. Acquires the tag write operations once and
 * returns a function that creates a tag (single, multi-segment, or continuation) via the
 * API and inserts it into the editor. Callers pass only *what* to tag — the persistence
 * ("how") is encapsulated here, not threaded in from sidebar code.
 */
export const useCreateAndInsertTag = (): ((input: CreateAndInsertTagInput) => void) => {
  const { mutate: createTag } = useCreateTag();
  const { mutate: continueTag } = useContinueTag();
  const { mutate: updateTag } = useUpdateTag();
  const { mutateAsync: discardTags } = useDiscardTags();

  return useCallback(
    (input: CreateAndInsertTagInput) => createAndInsertTag(input, { createTag, continueTag, updateTag, discardTags }),
    [createTag, continueTag, updateTag, discardTags]
  );
};

const isPlaced = (editor: Editor, tagId: string) =>
  !Editor.nodes(editor, { at: [], match: (n) => isTag(n) && n.tagId === tagId }).next().done || !!findBlockCarrierForTag(editor, tagId);

const discardUnplaced = (editor: Editor, tagIds: string[], documentSuperId: number, discardTags: TagPersistence['discardTags']) => {
  const ids = tagIds.filter((id) => !isPlaced(editor, id));
  if (ids.length) discardTags({ ids, documentSuperId }).catch((error) => console.error('Error discarding unplaced tags', error));
};

/**
 * Create a tag via API and insert it into the editor.
 * Handles continuation tags by updating the original tag's nextSegment.
 */
const createAndInsertTag = (
  { editor, tagType, tagData, continuationTag = null, successorTag = null, onContinuationComplete, callbacks = {} }: CreateAndInsertTagInput,
  persistence: TagPersistence
): void => {
  const { createTag, continueTag, discardTags } = persistence;

  // Restore the last known selection if the editor lost focus (e.g. user clicked in sidebar)
  if (!editor.selection && editor.lastSelection) {
    Transforms.select(editor, editor.lastSelection);
  }

  if (!editor.selection || Range.isCollapsed(editor.selection)) {
    console.warn('Cannot create tag: no selection or selection is collapsed');
    return;
  }

  const isContinuation = !!continuationTag;
  const hasSuccessor = !!successorTag;
  const segmentCount = calculateTagSegments(editor);

  if (segmentCount === 0) {
    console.warn('Cannot create tag: no segments calculated');
    return;
  }

  // For overlapping tags, we need to create multiple API records
  if (segmentCount > 1) {
    createMultiSegmentTag(
      {
        editor,
        tagType,
        tagData,
        segmentCount,
        continuationTag,
        successorTag,
        onContinuationComplete,
        callbacks,
      },
      persistence
    );
    return;
  }

  // Continuation path: use atomic continueTag (backend handles predecessor/successor updates)
  if (isContinuation || hasSuccessor) {
    continueTag(
      {
        ...tagData,
        predecessorId: continuationTag?.tagId || undefined,
        successorId: successorTag?.tagId || undefined,
      },
      {
        onError: (error) => {
          console.error('Error creating continuation tag', error);
          callbacks.onError?.(error);
        },
        onSuccess: (newTag) => {
          if (isContinuation) {
            onContinuationComplete?.();
          }
          insertTag(editor, tagType, isContinuation ? continuationTag : null, newTag.id, successorTag?.tagId ?? null);
          discardUnplaced(editor, [newTag.id], tagData.documentSuperId, discardTags);
          callbacks.onSuccess?.(newTag);
        },
      }
    );
    return;
  }

  // Non-continuation: simple create
  createTag(tagData, {
    onError: (error) => {
      console.error('Error creating tag', error);
      callbacks.onError?.(error);
    },
    onSuccess: (newTag) => {
      insertTag(editor, tagType, null, newTag.id, null);
      discardUnplaced(editor, [newTag.id], tagData.documentSuperId, discardTags);
      callbacks.onSuccess?.(newTag);
    },
  });
};

interface CreateMultiSegmentTagInput {
  editor: CreateAndInsertTagInput['editor'];
  tagType: string;
  tagData: CreateTagInput;
  segmentCount: number;
  continuationTag?: ContinuationTagRef | null;
  successorTag?: SuccessorTagRef | null;
  onContinuationComplete?: () => void;
  callbacks?: {
    onSuccess?: (newTag: { id: string }) => void;
    onError?: (error: unknown) => void;
  };
}

/**
 * Create multiple tag segments via API for overlapping tag scenarios.
 * Creates all API records first, then inserts into Slate with the correct IDs.
 */
const createMultiSegmentTag = async (
  { editor, tagType, tagData, segmentCount, continuationTag, successorTag = null, onContinuationComplete, callbacks = {} }: CreateMultiSegmentTagInput,
  persistence: TagPersistence
): Promise<void> => {
  const { createTag, updateTag, discardTags } = persistence;
  const isContinuation = !!continuationTag;
  const hasSuccessor = !!successorTag;
  const createdTags: { id: string }[] = [];

  // Helper to create a plain segment (no continuation linkage)
  const createSegment = (index: number): Promise<{ id: string }> => {
    return new Promise((resolve, reject) => {
      createTag(tagData, {
        onError: (error) => {
          console.error(`Error creating tag segment ${index}`, error);
          reject(error);
        },
        onSuccess: (newTag) => {
          createdTags.push(newTag);
          resolve(newTag);
        },
      });
    });
  };

  try {
    // Create all segments sequentially as plain tags first
    for (let i = 0; i < segmentCount; i++) {
      await createSegment(i);
    }

    // Link internal segments via nextSegment (each segment points to the next)
    for (let i = 0; i < createdTags.length - 1; i++) {
      await new Promise<void>((resolve, reject) => {
        updateTag(
          {
            id: createdTags[i].id,
            nextSegment: createdTags[i + 1].id,
            tailSegment: false,
          },
          {
            onError: reject,
            onSuccess: () => resolve(),
          }
        );
      });
    }

    // Handle continuation linkage for the last segment and predecessor atomically
    if (isContinuation || hasSuccessor) {
      // Link predecessor -> first segment
      if (isContinuation && continuationTag?.tagId) {
        await new Promise<void>((resolve, reject) => {
          updateTag(
            {
              id: continuationTag.tagId,
              nextSegment: createdTags[0].id,
              tailSegment: false,
            },
            {
              onError: (error) => {
                console.error('Error updating original tag nextSegment', error);
                reject(error);
              },
              onSuccess: () => resolve(),
            }
          );
        });
      }

      if (successorTag) {
        await new Promise<void>((resolve, reject) => {
          updateTag(
            {
              id: createdTags[createdTags.length - 1].id,
              nextSegment: successorTag.tagId,
              tailSegment: false,
            },
            { onError: reject, onSuccess: () => resolve() }
          );
        });
      } else {
        // Mark last segment as tail
        await new Promise<void>((resolve, reject) => {
          updateTag(
            {
              id: createdTags[createdTags.length - 1].id,
              tailSegment: true,
            },
            { onError: reject, onSuccess: () => resolve() }
          );
        });
      }

      if (isContinuation && continuationTag?.tagId) onContinuationComplete?.();
    }

    // Insert tags into Slate with the API's ids
    const tagIds = createdTags.map((t) => t.id);
    insertTagWithIds(editor, tagType, continuationTag || null, tagIds, successorTag?.tagId ?? null);
    discardUnplaced(editor, tagIds, tagData.documentSuperId, discardTags);

    callbacks.onSuccess?.(createdTags[0]);
  } catch (error) {
    console.error('Error creating multi-segment tag', error);
    discardUnplaced(
      editor,
      createdTags.map((t) => t.id),
      tagData.documentSuperId,
      discardTags
    );
    callbacks.onError?.(error);
  }
};
